// Pick a photo: the spa's photo library, photos Jen uploaded, or a new upload (cropped to fit the spot).

import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../lib/api';
import { Button, Modal, Segmented, Spinner, toastError } from '../lib/ui';
import { Icon } from '../lib/icons';
import { preparePhoto } from '../lib/imageResize';
import type { Img } from '../../shared/blocks';

interface Stock {
  base: string;
  alt: string;
}
interface Uploaded {
  id: string;
  name: string;
  width: number;
  height: number;
}

let stockCache: Stock[] | null = null;

/** The stock file that best fits the slot's shape. */
function stockImg(s: Stock, aspect: number | null): Img {
  if (aspect && aspect < 0.9) return { src: `/stock/${s.base}-t.jpg`, alt: s.alt, width: 800, height: 1000 };
  if (aspect && aspect < 1.2) return { src: `/stock/${s.base}-s.jpg`, alt: s.alt, width: 600, height: 600 };
  return { src: `/stock/${s.base}-w.jpg`, alt: s.alt, width: 1200, height: 800 };
}

function shapeName(aspect: number | null): string {
  if (!aspect) return '';
  if (aspect > 1.2) return 'wide';
  if (aspect < 0.9) return 'tall';
  return 'square';
}

export const isPhotoFile = (f: File) => /^image\//.test(f.type) || /\.(jpe?g|png|heic|webp)$/i.test(f.name);

/** Shrinks and crops a photo to the slot's shape, uploads it, and returns it ready to place. */
export async function uploadPhoto(file: File, aspect: number | null): Promise<Img> {
  if (!isPhotoFile(file)) throw new Error('That file is not a photo.');
  const p = await preparePhoto(file, aspect);
  const form = new FormData();
  form.append('file', new File([p.blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }));
  form.append('width', String(p.width));
  form.append('height', String(p.height));
  form.append('name', file.name);
  const r = await api<{ src: string; width: number; height: number; name: string }>('/api/images', { form });
  return { src: r.src, alt: '', width: r.width, height: r.height };
}

export function ImagePicker({ aspect, onPick, onClose }: { aspect: number | null; onPick: (img: Img) => void; onClose: () => void }) {
  const [tab, setTab] = useState<'mine' | 'library'>('library');
  const [stock, setStock] = useState<Stock[] | null>(stockCache);
  const [mine, setMine] = useState<Uploaded[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  // Each pick gets a number. An upload that finishes after another pick, or after the picker closed, is ignored.
  const op = useRef(0);
  const choose = (img: Img) => {
    op.current++;
    onPick(img);
  };
  useEffect(
    () => () => {
      op.current++;
    },
    [],
  );

  useEffect(() => {
    if (!stockCache)
      fetch('/stock/index.json')
        .then((r) => r.json())
        .then((d: Stock[]) => setStock((stockCache = d)))
        .catch(() => setStock([]));
    api<{ images: Uploaded[] }>('/api/images')
      .then((r) => {
        setMine(r.images);
        if (r.images.length) setTab('mine');
      })
      .catch(() => setMine([]));
  }, []);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    const mine = ++op.current;
    setBusy(true);
    try {
      const img = await uploadPhoto(file, aspect);
      if (mine === op.current) onPick(img);
    } catch (e) {
      if (mine !== op.current) return;
      toastError(e);
      setBusy(false);
    }
  };

  return (
    <Modal title="Choose a photo" onClose={onClose} wide>
      <div
        class={`drop ${drag ? 'drag' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          upload(e.dataTransfer?.files[0]);
        }}
      >
        {busy ? (
          <Spinner label="Fitting your photo…" />
        ) : (
          <>
            <Icon name="upload" size={24} />
            <div>
              <b>Use your own photo</b>
              <small>Drag it here, or pick one. {shapeName(aspect) && `It's trimmed to a ${shapeName(aspect)} shape automatically.`}</small>
            </div>
            <Button kind="primary" onClick={() => input.current?.click()}>
              Pick a photo
            </Button>
            <input ref={input} type="file" accept="image/jpeg,image/png,image/heic,image/webp" hidden onChange={(e) => upload(e.currentTarget.files?.[0])} />
          </>
        )}
      </div>

      <div class="picker-tabs">
        <Segmented
          label="Photo source"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'library', label: 'LaVir photos' },
            { value: 'mine', label: `Your uploads${mine?.length ? ` (${mine.length})` : ''}` },
          ]}
        />
      </div>

      {tab === 'library' &&
        (!stock ? (
          <Spinner />
        ) : (
          <div class="photo-grid">
            {stock.map((s) => {
              const img = stockImg(s, aspect);
              return (
                <button key={s.base} class="photo" onClick={() => choose(img)} title={s.alt}>
                  <img src={`/stock/${s.base}-s.jpg`} alt={s.alt} loading="lazy" />
                </button>
              );
            })}
          </div>
        ))}

      {tab === 'mine' &&
        (!mine ? (
          <Spinner />
        ) : mine.length === 0 ? (
          <p class="muted pad">Photos you upload show up here, so you can use them again.</p>
        ) : (
          <div class="photo-grid">
            {mine.map((m) => (
              <button key={m.id} class="photo" onClick={() => choose({ src: `/img/${m.id}`, alt: '', width: m.width, height: m.height })} title={m.name}>
                <img src={`/img/${m.id}`} alt={m.name} loading="lazy" />
              </button>
            ))}
          </div>
        ))}
    </Modal>
  );
}
